import { DialogComponent } from '@theme/dialog';

const quickViewTriggerSelector = '[data-gvs-quick-view-trigger]';

/**
 * Set plain text and return whether useful content was supplied.
 * @param {Element | null} element
 * @param {string | undefined} value
 * @returns {boolean}
 */
function setText(element, value) {
  if (!(element instanceof HTMLElement)) return false;

  const cleanValue = value?.trim() || '';
  element.textContent = cleanValue;
  element.hidden = cleanValue.length === 0;
  return cleanValue.length > 0;
}

/**
 * Populate the key-details list.
 * @param {Element | null} list
 * @param {string | undefined} rawSpecs
 * @returns {number}
 */
function populateSpecs(list, rawSpecs) {
  if (!(list instanceof HTMLUListElement)) return 0;

  list.replaceChildren();

  const specs = (rawSpecs || '')
    .split('|||')
    .map((spec) => spec.trim())
    .filter(Boolean);

  specs.forEach((spec) => {
    const item = document.createElement('li');
    const marker = document.createElement('span');
    const copy = document.createElement('span');

    marker.className = 'gvs-quick-view__spec-marker';
    marker.setAttribute('aria-hidden', 'true');
    marker.textContent = '✓';
    copy.textContent = spec;

    item.append(marker, copy);
    list.appendChild(item);
  });

  return specs.length;
}

/**
 * Populate an optional definition-list row.
 * @param {Element | null} row
 * @param {Element | null} valueElement
 * @param {string | undefined} value
 * @returns {boolean}
 */
function populateReference(row, valueElement, value) {
  const hasValue = setText(valueElement, value);
  if (row instanceof HTMLElement) row.hidden = !hasValue;
  return hasValue;
}

class GVSQuickViewDialog extends DialogComponent {
  /** @type {number} */
  #imageRequestId = 0;

  /**
   * Reset and load the selected product image without leaving the previous
   * included product visible while a new asset downloads.
   * @param {HTMLDialogElement} dialog
   * @param {DOMStringMap} data
   */
  #populateImage(dialog, data) {
    const media = dialog.querySelector('[data-gvs-quick-view-media]');
    const image = dialog.querySelector('[data-gvs-quick-view-image]');
    const placeholder = dialog.querySelector('[data-gvs-quick-view-image-placeholder]');
    const body = dialog.querySelector('[data-gvs-quick-view-body]');
    const requestId = ++this.#imageRequestId;
    const source = data.productImage?.trim() || '';

    if (!(media instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return;

    const showPlaceholder = () => {
      if (requestId !== this.#imageRequestId) return;
      media.dataset.imageState = 'missing';
      media.setAttribute('aria-busy', 'false');
      image.hidden = true;
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.alt = '';
      if (placeholder instanceof HTMLElement) placeholder.hidden = false;
      if (body instanceof HTMLElement) body.classList.add('gvs-quick-view__body--media-missing');
    };

    if (!source) {
      showPlaceholder();
      return;
    }

    media.dataset.imageState = 'loading';
    media.setAttribute('aria-busy', 'true');
    image.hidden = false;
    image.alt = data.productImageAlt || data.productTitle || '';
    image.decoding = 'async';
    image.loading = 'eager';
    image.sizes = '(min-width: 750px) 44vw, calc(100vw - 40px)';
    if (data.productImageSrcset) image.srcset = data.productImageSrcset;
    if (placeholder instanceof HTMLElement) placeholder.hidden = true;
    if (body instanceof HTMLElement) body.classList.remove('gvs-quick-view__body--media-missing');

    image.onload = () => {
      if (requestId !== this.#imageRequestId) return;
      media.dataset.imageState = 'ready';
      media.setAttribute('aria-busy', 'false');
    };

    image.onerror = showPlaceholder;
    image.src = source;

    if (image.complete) {
      if (image.naturalWidth > 0) image.onload?.(new Event('load'));
      else showPlaceholder();
    }
  }

  /**
   * Populate all quick-view fields from the card trigger.
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement} trigger
   */
  #populate(dialog, trigger) {
    const data = trigger.dataset;
    const panel = dialog.querySelector('.gvs-quick-view__body');
    if (panel instanceof HTMLElement) panel.scrollTop = 0;

    const hasType = setText(dialog.querySelector('[data-gvs-quick-view-type]'), data.productType);
    setText(dialog.querySelector('[data-gvs-quick-view-title]'), data.productTitle);
    const hasIntro = setText(dialog.querySelector('[data-gvs-quick-view-intro]'), data.productIntro);

    const quantity = Math.max(1, Number.parseInt(data.productQuantity || '1', 10) || 1);
    const headerQuantity = dialog.querySelector('[data-gvs-quick-view-header-quantity]');
    if (headerQuantity instanceof HTMLElement) {
      setText(headerQuantity, `${quantity} ${quantity === 1 ? 'item' : 'items'} in kit`);
      headerQuantity.dataset.mobileLabel = `Included ×${quantity}`;
    }
    setText(dialog.querySelector('[data-gvs-quick-view-quantity]'), String(quantity));
    setText(
      dialog.querySelector('[data-gvs-quick-view-quantity-copy]'),
      quantity === 1
        ? 'One supplied as part of the configuration shown on this page.'
        : `${quantity} supplied as part of the configuration shown on this page.`,
    );
    setText(dialog.querySelector('[data-gvs-quick-view-media-badge]'), `Included ×${quantity}`);

    const availability = dialog.querySelector('[data-gvs-quick-view-availability]');
    if (availability instanceof HTMLElement) {
      const isAvailable = data.productAvailable === 'true';
      availability.textContent = isAvailable ? 'Available separately' : 'Check separate availability';
      availability.dataset.state = isAvailable ? 'available' : 'check';
      availability.hidden = false;
    }

    const variantRow = dialog.querySelector('[data-gvs-quick-view-variant-row]');
    const skuRow = dialog.querySelector('[data-gvs-quick-view-sku-row]');
    const hasVariant = populateReference(
      variantRow,
      dialog.querySelector('[data-gvs-quick-view-variant]'),
      data.productVariantTitle,
    );
    const hasSku = populateReference(skuRow, dialog.querySelector('[data-gvs-quick-view-sku]'), data.productSku);
    const references = dialog.querySelector('[data-gvs-quick-view-references]');
    if (references instanceof HTMLElement) references.hidden = !(hasVariant || hasSku);

    const taxable = data.productTaxable === 'true';
    const price = dialog.querySelector('[data-gvs-quick-view-price]');
    const incPrice = dialog.querySelector('[data-gvs-quick-view-price-inc]');
    const incLabel = dialog.querySelector('[data-gvs-quick-view-price-inc-label]');
    const exPrice = dialog.querySelector('[data-gvs-quick-view-price-ex]');
    const exPriceRow = dialog.querySelector('[data-gvs-quick-view-price-ex-row]');
    const hasPrice = Boolean(data.productPriceInc?.trim());

    if (incPrice) incPrice.textContent = data.productPriceInc || '';
    if (incLabel) incLabel.textContent = taxable ? 'inc VAT' : 'VAT not applicable';
    if (exPrice) exPrice.textContent = data.productPriceEx || '';
    if (exPriceRow instanceof HTMLElement) exPriceRow.hidden = !taxable || !hasPrice;
    if (price instanceof HTMLElement) price.hidden = !hasPrice;

    const specCount = populateSpecs(dialog.querySelector('[data-gvs-quick-view-specs]'), data.keySpecs);
    const specSection = dialog.querySelector('[data-gvs-quick-view-spec-section]');
    if (specSection instanceof HTMLElement) specSection.hidden = specCount === 0;

    const fallback = dialog.querySelector('[data-gvs-quick-view-fallback]');
    if (fallback instanceof HTMLElement) fallback.hidden = hasIntro || specCount > 0;

    const productLink = dialog.querySelector('[data-gvs-quick-view-link]');
    if (productLink instanceof HTMLAnchorElement) {
      const productUrl = data.productUrl?.trim() || '';
      if (productUrl) {
        productLink.href = productUrl;
        productLink.hidden = false;
      } else {
        productLink.removeAttribute('href');
        productLink.hidden = true;
      }
      productLink.setAttribute('aria-label', `View full product details for ${data.productTitle || 'included product'}`);
    }

    // Avoid a lone separator/meta row when the product type is blank and the
    // availability chip is the only item.
    const metaRow = dialog.querySelector('.gvs-quick-view__meta-row');
    if (metaRow instanceof HTMLElement) metaRow.classList.toggle('gvs-quick-view__meta-row--single', !hasType);

    this.#populateImage(dialog, data);
  }

  /**
   * Populate and open this included-product view through the shared Horizon
   * dialog lifecycle.
   * @param {HTMLElement} trigger
   */
  async openFromTrigger(trigger) {
    const { dialog } = this.refs;
    this.#populate(dialog, trigger);

    const otherQuickViews = document.querySelectorAll('gvs-quick-view-dialog dialog[open]');
    const closeTasks = [];

    otherQuickViews.forEach((openDialog) => {
      if (openDialog === dialog) return;
      const component = openDialog.closest('gvs-quick-view-dialog');
      if (component instanceof GVSQuickViewDialog) closeTasks.push(component.closeDialog());
    });

    if (closeTasks.length) await Promise.allSettled(closeTasks);
    if (!this.isConnected || dialog.open) return;

    this.showDialog(trigger);

    // The shared dialog lifecycle may focus the close control while the browser
    // is still resolving the mobile sheet geometry. Re-assert the intended
    // starting position after opening so the media badge cannot sit beneath
    // the sticky header by a few pixels. The second frame also covers late
    // layout adjustment when the responsive image begins loading.
    const body = dialog.querySelector('[data-gvs-quick-view-body]');
    const resetBodyScroll = () => {
      if (dialog.open && body instanceof HTMLElement) {
        body.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };

    requestAnimationFrame(() => {
      resetBodyScroll();
      requestAnimationFrame(resetBodyScroll);
    });
  }

  disconnectedCallback() {
    this.#imageRequestId += 1;
    super.disconnectedCallback();
  }
}

if (!customElements.get('gvs-quick-view-dialog')) {
  customElements.define('gvs-quick-view-dialog', GVSQuickViewDialog);
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest(quickViewTriggerSelector);
  if (!(trigger instanceof HTMLElement)) return;

  const dialogId = trigger.getAttribute('aria-controls');
  const dialog = dialogId ? document.getElementById(dialogId) : null;
  const component = dialog?.closest('gvs-quick-view-dialog');

  if (
    !(dialog instanceof HTMLDialogElement) ||
    !(component instanceof GVSQuickViewDialog) ||
    typeof dialog.showModal !== 'function'
  ) {
    if (trigger.dataset.productUrl) window.location.assign(trigger.dataset.productUrl);
    return;
  }

  event.preventDefault();
  component.openFromTrigger(trigger);
});
